import { useInfiniteQuery } from '@tanstack/react-query';

import { PREFIX_URL } from '@/src/api/config';
import { axios } from '@/src/lib/axios/axios';
import { InfiniteQueryConfig } from '@/src/lib/react-query/react-query';
import { generateRoutePath } from '@/src/routes';

import { INBOX_API_KEY } from '../../../../api/config';
import { EmailDeliveryType } from '../../../../types';

// const url = `${PREFIX_URL}/v01/emailAttachment`;
const url = `${PREFIX_URL}/inbox-threads/:inboxEntryId/attachments`;

type EmailThreadAttachmentsResponse = {
  data: {
    attachments: any[];
    'total-count': number;
    page: number;
    limit: number;
  };
  status: boolean;
};

type GetEmailThreadAttachmentsDTO = {
  inboxEntryId: number;
  projectId: string;
  params: {
    page: number;
    limit: number;
    type: EmailDeliveryType;
  };
};

export const getEmailThreadAttachments = ({
  inboxEntryId,
  projectId,
  params,
}: GetEmailThreadAttachmentsDTO): Promise<EmailThreadAttachmentsResponse> => {
  return axios.get(generateRoutePath(url, { projectId, inboxEntryId }), {
    params,
  });
};

export const EMAIL_THREAD_ATTACHMENTS_LIMIT = 50;

export const emailThreadAttachmentsKeys = {
  all: ['emailThreadAttachments'] as const,
  lists: ({
    inboxEntryId,
    projectId,
    limit = EMAIL_THREAD_ATTACHMENTS_LIMIT,
    type,
  }: {
    inboxEntryId: number;
    projectId: string;
    type: EmailDeliveryType;
    limit?: number;
  }) =>
    [
      INBOX_API_KEY,
      ...emailThreadAttachmentsKeys.all,
      projectId,
      inboxEntryId,
      limit,
      type,
    ] as const,
};

type QueryFnType = typeof getEmailThreadAttachments;

type useEmailThreadAttachmentsOptions = {
  projectId: string;
  inboxEntryId: number;
  type: EmailDeliveryType;
  limit?: number;
  config?: InfiniteQueryConfig<QueryFnType>;
};

export const useEmailThreadAttachments = ({
  inboxEntryId,
  projectId,
  type,
  config,
  limit = EMAIL_THREAD_ATTACHMENTS_LIMIT,
}: useEmailThreadAttachmentsOptions) => {
  return useInfiniteQuery({
    queryKey: [
      INBOX_API_KEY,
      ...emailThreadAttachmentsKeys.all,
      projectId,
      inboxEntryId,
      limit,
      type,
    ],
    queryFn: ({ pageParam = 1 }) =>
      getEmailThreadAttachments({
        params: { page: pageParam, limit, type },
        projectId,
        inboxEntryId,
      }),
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      // Assuming lastPageData is an array of objects and totalCount is available
      const currentPageCount = allPages.length * limit;

      if (currentPageCount < lastPage.data['total-count']) {
        // If there are more items to fetch, return the next page number
        return allPages.length + 1;
      }

      // If all items have been fetched, return undefined to stop fetching
      return undefined;
    },
    ...config,
  });
};
